/**
 * VestingDetails.js - Main Vesting Progress Page
 * Displays comprehensive vesting information for TPHILE tokens
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { 
  ArrowLeft, 
  RefreshCw, 
  Clock, 
  Lock, 
  Unlock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  Loader,
  Coins,
  Calendar
} from 'lucide-react';

import VestingChart from '../components/VestingChart';
import VestingSummary from '../components/VestingSummary';
import VestingBreakdown from '../components/VestingBreakdown';
import VestingTimeline from '../components/VestingTimeline';
import VestingLoader from '../components/VestingLoader';

import { useAuth } from '../contexts/AuthContext';
import { ACTIVE_NETWORK } from '../config/tokenConfig';
import UserVestingABI from '../contracts/abis/UserVesting.json';

import { 
  determineVestingState, 
  formatTokenAmount,
  calculateTGEProgress,
  VESTING_DURATION_SECONDS,
  calculateVestingProgress,
  getCurrentYearAndRate
} from '../utils/vestingHelpers';
import { getUserVestingInfo } from '../utils/contractHelpers';

import './VestingDetails.css';

// Smart contract address
const USER_VESTING_CONTRACT = process.env.REACT_APP_USER_VESTING_CONTRACT || '0xBeB4A3CdCeE12AF82162636f7BD2dE835b87fB87';

const VestingDetails = () => {
  const navigate = useNavigate();
  const { token: userToken, isLoggedIn } = useAuth();
  
  // State management
  const [vestingState, setVestingState] = useState('loading'); // 'loading', 'pre-tge', 'vesting', 'complete'
  const [vestingData, setVestingData] = useState(null);
  const [blockchainData, setBlockchainData] = useState(null);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Animation states
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  /**
   * Generate 7-milestone breakdown (TGE + 6 months) for interactive timeline
   */
  const getMonthlyBreakdown = () => {
    if (!vestingData) return [];
    
    const totalAllocated = vestingData.totalAllocated || 40000;
    const tgePercentage = 10; // 10% instant at TGE
    const tgeAmount = totalAllocated * (tgePercentage / 100);
    const vestingAmount = totalAllocated - tgeAmount; // 90% to vest over 6 months
    const monthlyAmount = vestingAmount / 6; // Equal monthly distribution

    const today = new Date();
    // Use a fixed TGE date for consistency
    const tgeDate = new Date('2025-10-13');
    
    return [
      {
        id: 'tge',
        label: 'TGE',
        fullLabel: 'TGE Activated',
        date: 'Oct 13, 2025',
        dateObj: tgeDate,
        amount: tgeAmount,
        percentage: tgePercentage,
        cumulativeAmount: tgeAmount,
        cumulativePercentage: tgePercentage,
        description: 'Instant unlock at Token Generation Event',
        status: 'completed',
        icon: CheckCircle2
      },
      ...Array.from({ length: 6 }, (_, i) => {
        const monthNumber = i + 1;
        const monthDate = new Date(tgeDate);
        monthDate.setMonth(monthDate.getMonth() + monthNumber);
        
        const isPast = today >= monthDate;
        const isCurrent = !isPast && today >= new Date(monthDate.getTime() - 30 * 24 * 60 * 60 * 1000);
        const cumulativeAmount = tgeAmount + (monthlyAmount * monthNumber);
        const cumulativePercentage = (cumulativeAmount / totalAllocated * 100);
        
        return {
          id: `month${monthNumber}`,
          label: `M${monthNumber}`,
          fullLabel: `Month ${monthNumber}`,
          date: monthDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          dateObj: monthDate,
          amount: monthlyAmount,
          percentage: Number((monthlyAmount / totalAllocated * 100).toFixed(1)),
          cumulativeAmount: cumulativeAmount,
          cumulativePercentage: Number(cumulativePercentage.toFixed(1)),
          description: `Linear daily unlock throughout month ${monthNumber}`,
          status: isPast ? 'completed' : isCurrent ? 'active' : 'upcoming',
          icon: isPast ? CheckCircle2 : isCurrent ? Clock : Lock
        };
      })
    ];
  };

  /**
   * Fetch data from blockchain contract
   */
  const fetchBlockchainData = useCallback(async (walletAddress) => {
    try {
      if (!window.ethereum) {
        console.warn('No ethereum provider found');
        return null;
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const contract = new ethers.Contract(
        USER_VESTING_CONTRACT,
        UserVestingABI.abi,
        provider
      );

      // Fetch contract data
      const [
        tgeActivated,
        tgeTimestamp,
        userRewards,
        userClaimable,
        tgeProgress
      ] = await Promise.all([
        contract.tgeActivated(),
        contract.tgeTimestamp(),
        walletAddress ? contract.userRewards(walletAddress) : null,
        walletAddress ? contract.getUserClaimable(walletAddress) : null,
        contract.getTGEProgress()
      ]);

      const data = {
        tgeActivated,
        tgeTimestamp: Number(tgeTimestamp),
        vestingComplete: tgeActivated && Date.now() / 1000 > Number(tgeTimestamp) + VESTING_DURATION_SECONDS, // 6 months
        userRewards: userRewards ? {
          totalEarned: formatTokenAmount(userRewards.totalEarned),
          instantAmount: formatTokenAmount(userRewards.instantAmount),
          vestedAmount: formatTokenAmount(userRewards.vestedAmount),
          claimedInstant: formatTokenAmount(userRewards.claimedInstant),
          claimedVested: formatTokenAmount(userRewards.claimedVested),
          vestingStart: Number(userRewards.vestingStart),
          vestingEnd: Number(userRewards.vestingEnd)
        } : null,
        claimable: userClaimable ? {
          instantClaimable: formatTokenAmount(userClaimable.instantClaimable),
          vestedClaimable: formatTokenAmount(userClaimable.vestedClaimable)
        } : null,
        tgeProgress: {
          processedUsers: Number(tgeProgress.processedUsers),
          totalUsers: Number(tgeProgress.totalUsers),
          snapshotComplete: tgeProgress.snapshotComplete,
          activated: tgeProgress.activated
        }
      };

      return data;
    } catch (error) {
      console.error('Error fetching blockchain data:', error);
      return null;
    }
  }, []);

  /**
   * Fetch data from backend API
   */
  const fetchBackendData = useCallback(async () => {
    try {
      if (!userToken) {
        console.warn('No user token available');
        return null;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/vesting-summary`,
        {
          headers: {
            'Authorization': `Bearer ${userToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch vesting data');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching backend data:', error);
      return null;
    }
  }, [userToken]);

  /**
   * Main data fetch function
   */
  const fetchVestingData = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);

    try {
      // Fetch real data from backend and blockchain
      const [backendData, walletAddress] = await Promise.all([
        fetchBackendData(),
        window.ethereum?.request({ method: 'eth_accounts' }).then(accounts => accounts?.[0] || null).catch(() => null)
      ]);

      let chainData = null;
      if (walletAddress && window.ethereum) {
        chainData = await fetchBlockchainData(walletAddress);
        
        // If we have contract data, use it to enhance backend data
        if (chainData && chainData.tgeActivated) {
          const contractInfo = await getUserVestingInfo(
            new ethers.Contract(USER_VESTING_CONTRACT, UserVestingABI.abi, new ethers.BrowserProvider(window.ethereum)),
            walletAddress
          );
          
          if (contractInfo) {
            // Merge contract data with backend data
            const mergedData = {
              ...backendData,
              totalAllocated: parseFloat(contractInfo.totalRewards) || backendData?.totalAllocated || 0,
              instantRewards: parseFloat(contractInfo.instantRewards) || 0,
              vestedRewards: parseFloat(contractInfo.vestedRewards) || 0,
              availableInstant: parseFloat(contractInfo.availableInstant) || 0,
              availableVested: parseFloat(contractInfo.availableVested) || 0,
              vestingProgress: contractInfo.vestingProgress,
              currentYear: contractInfo.currentYear,
              currentBaseRate: contractInfo.currentBaseRate
            };
            
            setVestingData(mergedData);
          }
        }
      }

      if (backendData || chainData) {
        const state = determineVestingState(backendData, chainData);
        
        if (!chainData || !chainData.tgeActivated) {
          setVestingData(backendData);
        }
        setBlockchainData(chainData);
        setVestingState(state);
      } else {
        setError('Unable to fetch vesting data. Please connect your wallet or try again.');
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error in fetchVestingData:', err);
      setError('An error occurred while loading vesting data.');
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchBackendData, fetchBlockchainData]);

  // Initial data fetch
  useEffect(() => {
    fetchVestingData();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchVestingData, 30000);
    
    // Page load animation
    setTimeout(() => setIsPageLoaded(true), 100);
    
    return () => clearInterval(interval);
  }, [fetchVestingData]);

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  /**
   * Render state indicator banner
   */
  const renderStateIndicator = () => {
    const stateConfig = {
      'loading': {
        icon: <Loader className="animate-spin" size={24} />,
        label: 'Loading...',
        className: 'state-loading'
      },
      'pre-tge': {
        icon: <Clock size={24} />,
        label: 'TGE Not Yet Triggered',
        className: 'state-pre-tge'
      },
      'vesting': {
        icon: <Lock size={24} />,
        label: 'Vesting in Progress',
        className: 'state-vesting'
      },
      'complete': {
        icon: <CheckCircle size={24} />,
        label: 'Vesting Complete',
        className: 'state-complete'
      }
    };

    const config = stateConfig[vestingState] || stateConfig['loading'];

    return (
      <div className={`state-indicator ${config.className}`}>
        {config.icon}
        <span>{config.label}</span>
      </div>
    );
  };

  /**
   * Render TGE progress for pre-TGE state
   */
  const renderTGEProgress = () => {
    if (vestingState !== 'pre-tge' || !vestingData?.tgeStatus) return null;

    const { currentUsers, currentPredictions, targetUsers, targetPredictions } = vestingData.tgeStatus;
    const progress = calculateTGEProgress(currentUsers, currentPredictions);

    return (
      <div className="tge-progress-section">
        <h3><AlertCircle size={20} /> TGE Requirements Progress</h3>
        <div className="tge-requirements">
          <div className="requirement">
            <div className="requirement-header">
              <span>Users</span>
              <span>{currentUsers?.toLocaleString()} / {(targetUsers || 10000).toLocaleString()}</span>
            </div>
            <div className="requirement-bar">
              <div 
                className="requirement-fill"
                style={{ width: `${progress.usersProgress}%` }}
              />
            </div>
            <span className="requirement-percentage">{progress.usersProgress.toFixed(1)}%</span>
          </div>
          <div className="requirement">
            <div className="requirement-header">
              <span>Predictions</span>
              <span>{currentPredictions?.toLocaleString()} / {(targetPredictions || 100000).toLocaleString()}</span>
            </div>
            <div className="requirement-bar">
              <div 
                className="requirement-fill"
                style={{ width: `${progress.predictionsProgress}%` }}
              />
            </div>
            <span className="requirement-percentage">{progress.predictionsProgress.toFixed(1)}%</span>
          </div>
        </div>
        <div className="tge-note">
          <Calendar size={16} />
          <span>TGE will trigger when both thresholds are met</span>
        </div>
      </div>
    );
  };

  // Loading state - use new VestingLoader with golden crescent moon
  if (vestingState === 'loading' && !vestingData) {
    return <VestingLoader />;
  }

  // Error state
  if (error && !vestingData) {
    return (
      <div className="vesting-details-page error-state">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>Unable to Load Data</h2>
          <p>{error}</p>
          <button onClick={fetchVestingData} className="retry-button">
            <RefreshCw size={20} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`vesting-details-page ${isPageLoaded ? 'loaded' : ''}`}>
      {/* Background effects */}
      <div className="vesting-background">
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
        <div className="grid-pattern" />
      </div>

      {/* Header Section */}
      <header className="vesting-header">
        <button 
          onClick={() => navigate('/login', { state: { activeTab: 'tphile' } })} 
          className="back-button"
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>
        
        <div className="header-content">
          <h1>
            <Coins size={36} />
            Vesting Progress
          </h1>
          {renderStateIndicator()}
        </div>

        <div className="header-actions">
          <button 
            onClick={fetchVestingData} 
            className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
            disabled={isRefreshing}
          >
            <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
            {isRefreshing ? 'Updating...' : 'Refresh'}
          </button>
          {lastUpdated && (
            <span className="last-updated">
              <Clock size={14} />
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </header>

      {/* TGE Progress (Pre-TGE only) */}
      {renderTGEProgress()}

      {/* Summary Cards */}
      <VestingSummary 
        data={vestingData} 
        state={vestingState}
        blockchainData={blockchainData}
      />

      {/* Time Series Chart */}
      <section className="chart-section">
        <div className="section-header">
          <h2>
            <TrendingUp size={24} />
            Token Distribution Timeline
          </h2>
        </div>
        <VestingChart 
          data={vestingData}
          state={vestingState}
        />
      </section>

      {/* Breakdown Section */}
      <VestingBreakdown 
        data={vestingData}
        state={vestingState}
      />

      {/* 7-MILESTONE INTERACTIVE TIMELINE WITH HOVER TOOLTIPS */}
      {vestingState !== 'pre-tge' && vestingData && (
        <div className="vesting-timeline-horizontal">
          <div className="timeline-header">
            <h2 className="timeline-title">
              <Clock size={24} />
              6-Month Vesting Timeline
            </h2>
            <div className="timeline-progress-indicator">
              <span className="progress-label">Progress:</span>
              <span className="progress-value">
                {vestingData.vestingStatus?.percentComplete?.toFixed(1) || '24.7'}%
              </span>
            </div>
          </div>
          
          <div className="timeline-progress-bar">
            <div 
              className="timeline-progress-fill"
              style={{ width: `${vestingData.vestingStatus?.percentComplete || 24.7}%` }}
            >
              <span className="progress-indicator-dot">Now</span>
            </div>
          </div>

          <div className="timeline-milestones-interactive">
            {getMonthlyBreakdown().map((milestone, index) => {
              const MilestoneIcon = milestone.icon;
              const isLast = index === 6;
              
              return (
                <div 
                  key={milestone.id}
                  className={`timeline-milestone-interactive ${milestone.status}`}
                >
                  {/* Connector Line (except for last milestone) */}
                  {!isLast && <div className="milestone-connector" />}
                  
                  {/* Milestone Icon Circle */}
                  <div className="milestone-icon-circle">
                    <MilestoneIcon size={20} />
                  </div>

                  {/* Milestone Label (Always Visible) */}
                  <div className="milestone-label-fixed">
                    <span className="milestone-label-text">{milestone.label}</span>
                    <span className="milestone-date-text">{milestone.date}</span>
                  </div>

                  {/* Hover Tooltip (Shows on Hover) */}
                  <div className="milestone-tooltip">
                    <div className="tooltip-arrow" />
                    <div className="tooltip-content">
                      <h4 className="tooltip-title">{milestone.fullLabel}</h4>
                      <p className="tooltip-date">{milestone.date}</p>
                      
                      <div className="tooltip-stats">
                        <div className="tooltip-stat">
                          <span className="tooltip-stat-label">Amount:</span>
                          <span className="tooltip-stat-value">
                            {milestone.amount.toLocaleString()} TPHILE
                          </span>
                        </div>
                        
                        <div className="tooltip-stat">
                          <span className="tooltip-stat-label">Of Total:</span>
                          <span className="tooltip-stat-value">
                            {milestone.percentage}%
                          </span>
                        </div>

                        <div className="tooltip-stat">
                          <span className="tooltip-stat-label">Cumulative:</span>
                          <span className="tooltip-stat-value">
                            {milestone.cumulativePercentage}% unlocked
                          </span>
                        </div>
                      </div>

                      <p className="tooltip-description">{milestone.description}</p>

                      <div className={`tooltip-status-badge ${milestone.status}`}>
                        <MilestoneIcon size={14} />
                        <span>
                          {milestone.status === 'completed' ? 'Completed' : 
                           milestone.status === 'active' ? 'In Progress' : 'Upcoming'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Stats Below Timeline */}
          <div className="timeline-summary-stats">
            <div className="summary-stat">
              <CheckCircle2 size={18} className="summary-icon completed" />
              <span>Completed: {getMonthlyBreakdown().filter(m => m.status === 'completed').length} milestones</span>
            </div>
            <div className="summary-stat">
              <Clock size={18} className="summary-icon active" />
              <span>In Progress: {getMonthlyBreakdown().filter(m => m.status === 'active').length} milestone</span>
            </div>
            <div className="summary-stat">
              <Lock size={18} className="summary-icon upcoming" />
              <span>Upcoming: {getMonthlyBreakdown().filter(m => m.status === 'upcoming').length} milestones</span>
            </div>
          </div>
        </div>
      )}

      {/* Post-Vesting Message */}
      {vestingState === 'complete' && (
        <div className="post-vesting-message">
          <CheckCircle size={32} />
          <div>
            <h3>Vesting Period Complete!</h3>
            <p>All your tokens are now fully unlocked. Future rewards will be 100% instantly claimable.</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default VestingDetails;

